/**
 * useAdminModalHandler - Generic hook for admin modal async operations
 * Consolidates common pattern: loading state, toast notifications, error handling
 */

import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';

interface UseAdminModalHandlerOptions {
  onSuccess?: () => void;
  onClose?: () => void;
  successDuration?: number;
  errorDuration?: number;
}

interface UseAdminModalHandlerReturn {
  loading: boolean;
  handleSubmit: <T>(
    operation: () => Promise<T>,
    successMessage: string,
    errorMessage?: string
  ) => Promise<T | undefined>;
}

/**
 * Hook for handling admin modal async operations with toast notifications
 *
 * @example
 * const { loading, handleSubmit } = useAdminModalHandler({ onSuccess, onClose });
 *
 * const onSubmit = () => handleSubmit(
 *   () => adminService.blockUser(userId, true),
 *   `Successfully blocked ${username}`,
 *   'Failed to block user'
 * );
 */
export function useAdminModalHandler(
  options: UseAdminModalHandlerOptions = {}
): UseAdminModalHandlerReturn {
  const {
    onSuccess,
    onClose,
    successDuration = 3000,
    errorDuration = 5000,
  } = options;

  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async <T>(
    operation: () => Promise<T>,
    successMessage: string,
    errorMessage: string = 'Operation failed'
  ): Promise<T | undefined> => {
    try {
      setLoading(true);
      const result = await operation();

      toast({
        title: 'Success',
        description: successMessage,
        status: 'success',
        duration: successDuration,
        isClosable: true,
      });

      onSuccess?.();
      onClose?.();

      return result;
    } catch (error: any) {
      const detail = error.response?.data?.detail || errorMessage;

      toast({
        title: 'Error',
        description: detail,
        status: 'error',
        duration: errorDuration,
        isClosable: true,
      });

      return undefined;
    } finally {
      setLoading(false);
    }
  }, [toast, onSuccess, onClose, successDuration, errorDuration]);

  return { loading, handleSubmit };
}
